import React from 'react'
import { Button } from '@/components/ui/button'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { WalletCurrencyDto } from 'api/Wallet/Types/WalletCurrencyDto'
import {
  FormBaseButtonContainer,
  FormBaseContainer,
  FormBaseErrorMessage,
  FormBaseFieldBlock,
  FormBaseFieldGroup,
  FormBaseFieldLabel,
  FormBaseRow,
} from 'components/Form/Form.styles'
import { Field, Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

export interface AddInterestRateThresholdFormValues {
  currencyId: string
  maxAmount: number
  minInterestRate: number
  maxInterestRate: number
}

const AddInterestRateThresholdFormUI: React.FC<
  FormikProps<AddInterestRateThresholdFormValues> & OuterProps
> = (props) => {
  const { handleSubmit, setSubmitting, isSubmitting, errors } = props
  const handleSubmitForm = () => {
    setSubmitting(true)
    handleSubmit()
  }

  return (
    <FormBaseContainer>
      <Form>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Currency</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field component='select' name='currencyId'>
                <option value='' disabled>
                  None
                </option>
                {(props.currencies || []).map((currency) => (
                  <option key={currency.id} value={currency.id}>
                    {currency.id}
                  </option>
                ))}
              </Field>
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.currencyId && <FormBaseErrorMessage>{errors.currencyId}</FormBaseErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Max Amount</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field type='number' name='maxAmount' min='0' step='0.01' />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.maxAmount && <FormBaseErrorMessage>{errors.maxAmount}</FormBaseErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Min Interest Rate</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field type='number' name='minInterestRate' min='0' step='0.01' />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.minInterestRate && (
            <FormBaseErrorMessage>{errors.minInterestRate}</FormBaseErrorMessage>
          )}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Max Interest Rate</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field type='number' name='maxInterestRate' step='0.01' />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.maxInterestRate && (
            <FormBaseErrorMessage>{errors.maxInterestRate}</FormBaseErrorMessage>
          )}
        </FormBaseFieldBlock>
        <FormBaseButtonContainer>
          <Button disabled={isSubmitting} key='button' onClick={handleSubmitForm} type='button'>
            Submit
          </Button>
        </FormBaseButtonContainer>
      </Form>
    </FormBaseContainer>
  )
}

interface OuterProps {
  permissions: PermissionDto[]
  currencies: WalletCurrencyDto[]

  onSubmit(values: FormSubmitValues<AddInterestRateThresholdFormValues>): Promise<void>
}

export const AddInterestRateThresholdForm = withFormik<
  OuterProps,
  AddInterestRateThresholdFormValues
>({
  mapPropsToValues: () => {
    return {
      currencyId: 'USD',
      maxAmount: 0,
      minInterestRate: 0,
      maxInterestRate: 0,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<AddInterestRateThresholdFormValues> = {}
    if (!values.currencyId) {
      errors.currencyId = 'required'
    }
    if (!values.maxAmount) {
      errors.maxAmount = 'required'
    }
    if (values.minInterestRate === undefined) {
      errors.minInterestRate = 'required'
    }
    if (values.minInterestRate) {
      if (values.minInterestRate <= 0) {
        errors.minInterestRate = 'Min Interest rate should be greater than or equal 0'
      }
    }
    if (!values.maxInterestRate) {
      errors.maxInterestRate = 'required'
    }
    if (values.minInterestRate) {
      if (values.minInterestRate < 0) {
        errors.minInterestRate = 'Min Interest rate should be greater than 0'
      }
    }

    return errors
  },
  enableReinitialize: true,
})(AddInterestRateThresholdFormUI)
